import axios, { AxiosError, AxiosResponse } from 'axios';

import {
	OrdwayPlan,
	OrdwaySubscriptionParams,
	OrdwayPaymentInfoParams,
	ProductOrdwayAttributesParams,
	ProductOrdwayAttributes,
	OrdwayDraftSubscriptionUpdateParams,
	FormattedOrdwayCharge,
	UpdateOrdwayPlansParams,
} from '@/models/ordway';
import {
	OrgPaymentMethod,
	OrgPaymentMethodResponse,
	PaymentType,
	PaymentMethodCreateParams,
	PaymentMethodType,
} from '@/models/org-payment-method';
import { BillingApi, DirectPlansInData, Product, User } from '@/api';
import { Constants } from '@/common/Constants';

export abstract class OrdwayService {
	public static async fetchOrdwayPlans(productType: string): Promise<OrdwayPlan[]> {
		const plansResponse = await axios.get<{ ordwayPlans: OrdwayPlan[] }>(
			process.env.VUE_APP_API_URL.replace(/\/+$/, '') + `/ordway/plans/${productType}`,
		);
		return plansResponse.data.ordwayPlans;
	}

	public static async fetchProductOrdwayAttributes(
		productId: number,
		orgId: number,
	): Promise<AxiosResponse<ProductOrdwayAttributes>> {
		return await axios.get(this.baseUrl + `/ordway/productOrdwayAttributes/${productId}/${orgId}`);
	}

	public static async fetchPartnerProductOrdwayAttributes(
		productId: number,
		requiredPartner: string,
	): Promise<AxiosResponse<ProductOrdwayAttributes>> {
		return await axios.post(this.baseUrl + '/ordway/productOrdwayAttributes/partner', {
			productId,
			requiredPartner,
		});
	}

	public static async changeProductOrg(
		productId: number,
		newOrgId: number,
	): Promise<AxiosResponse<void>> {
		return await axios.post(this.baseUrl + '/ordway/subscription/changeCustomer', {
			productId,
			newOrgId,
		});
	}

	public static async updateSubscriptionCustomFields(
		ordwayDirectPlanId: string,
		product: Product,
		ordwayPartnerPlanId?: string,
		requiredPartner?: string,
	): Promise<void> {
		await axios.patch(this.baseUrl + '/ordway/subscription/updateCustomFields', {
			ordwaySubscriptionId: ordwayDirectPlanId,
			productId: product.id,
			ordwayPartnerPlanId,
			requiredPartner,
		});
	}

	public static async updateOrdwayCustomer(
		organisationName: string,
		orgId: number,
		tradingName?: string,
	): Promise<AxiosResponse<any>> {
		return await axios.patch(process.env.VUE_APP_API_URL.replace(/\/+$/, '') + '/ordway/customer', {
			name: organisationName,
			tradingName,
			levnoOrgId: orgId,
		});
	}

	public static async changeOrdwayContact(
		orgId: number,
		previousContactUserId: number,
		newContactUserId: number,
	): Promise<void> {
		await axios.patch(this.baseUrl + '/ordway/contact/change', {
			levnoOrgId: orgId,
			previousUserId: previousContactUserId,
			newUserId: newContactUserId,
		});
	}

	public static async updateOrdwayContact(user: User): Promise<AxiosResponse<any>> {
		const { id, name, phone, email } = user;
		return await axios.patch(this.baseUrl + '/ordway/contact', {
			userId: id,
			name,
			phone,
			email,
		});
	}

	public static async fetchSingleOrdwayPlan(planId: string): Promise<OrdwayPlan> {
		const planResponse = await axios.get<{ ordwayPlan: OrdwayPlan }>(
			this.baseUrl + `/ordway/plan/${planId}`,
		);
		return planResponse.data.ordwayPlan;
	}

	public static async syncOrdwayDraftSubscription(productId: number): Promise<AxiosResponse<any>> {
		return await axios.post(this.baseUrl + '/ordway/subscription/syncDraft', { productId });
	}

	public static async createOrdwaySubscription(
		subscriptionCreateParams: OrdwaySubscriptionParams,
	): Promise<any> {
		// TODO: Type this.
		return await axios.post(this.baseUrl + '/ordway/subscription/create', {
			...subscriptionCreateParams,
		});
	}

	public static async createProductOrdwayAttributes(
		productOrdwayAttributes: ProductOrdwayAttributesParams,
	): Promise<any> {
		// TODO: Type this.
		return await axios.post(this.baseUrl + '/products/storeProductOrdwayAttributes', {
			...productOrdwayAttributes,
		});
	}

	public static async updateSubscriptionQuantities(product: {
		siteId: number;
		orgId: number;
		id: number;
	}): Promise<void> {
		await axios.patch(this.baseUrl + '/ordway/subscription/chargeQuantities', {
			productId: product.id,
			...product,
		});
	}

	public static async updateProductOrdwayAttribute(
		allOrdwayPlans: OrdwayPlan[],
		product: { siteId: number; orgId: number; id: number; supplier: string },
		supplierHasChanged: boolean,
		productOrdwayAttributes: ProductOrdwayAttributes,
		directPlanId: string,
		partnerProductOrdwayAttributes: ProductOrdwayAttributes | null,
		partnerPlanId?: string,
	): Promise<{ message: string; error?: AxiosError }> {
		const selectedPlan = allOrdwayPlans.find((plan: OrdwayPlan) => plan.id === directPlanId);
		if (selectedPlan !== undefined) {
			productOrdwayAttributes.ordwayPlanId = selectedPlan.id;
			productOrdwayAttributes.ordwayPlanName = selectedPlan.name;
			try {
				await axios.patch(this.baseUrl + `/products/${product.id}/productOrdwayAttribute`, {
					...productOrdwayAttributes,
					productTier: selectedPlan.levno_product_tier,
				});
			} catch (error) {
				return {
					error,
					message:
						'Updating direct plan in Ordway failed, please retry and / or let the software team know.',
				};
			}
		}

		if (partnerProductOrdwayAttributes === null && partnerPlanId !== '') {
			// No previous partner plan attribute, need to create and store one - only case we are catering for right now.
			const selectedPartnerPlan = allOrdwayPlans.find(
				(plan: OrdwayPlan) => plan.id === partnerPlanId,
			);
			if (selectedPartnerPlan !== undefined) {
				try {
					await axios.post(
						this.baseUrl + `/products/${product.id}/partner/productOrdwayAttribute`,
						{
							requiredPartner: product.supplier,
							productId: product.id,
							ordwayPlanId: selectedPartnerPlan.id,
							ordwayPlanName: selectedPartnerPlan.name,
							ordwayPartnerPlanId: selectedPartnerPlan.id,
							ordwayPartnerPlanName: selectedPartnerPlan.name,
							productTier: selectedPartnerPlan.levno_product_tier,
						},
					);
					// await this.createProductOrdwayAttributes(partnerAttributeParams);
				} catch (error) {
					return {
						error,
						message:
							'Updating partner plan in Ordway failed, please retry and / or let the software team know.',
					};
				}
			}
		}
		return { message: 'Success' };
	}

	public static async fetchFormattedChargePrices(
		productId: number,
		ordwayPlanId: string,
	): Promise<FormattedOrdwayCharge[]> {
		const formattedChargesResponse: AxiosResponse<FormattedOrdwayCharge[]> = await axios.get(
			this.baseUrl + `/ordway/plan/${ordwayPlanId}/productChargePrices/${productId}`,
		);
		return formattedChargesResponse.data;
	}

	public static async addPaymentMethod(
		paymentMethodParams: PaymentMethodCreateParams,
	): Promise<AxiosResponse<any>> {
		return await axios.post(this.baseUrl + `/orgs/${paymentMethodParams.orgId}/paymentMethod`, {
			data: { ...paymentMethodParams },
		});
	}

	public static async fetchAdditionalPaymentMethodTypes(
		orgId: number,
	): Promise<AxiosResponse<{ data: PaymentMethodType[] }>> {
		return await axios.get<{ data: PaymentMethodType[] }>(
			this.baseUrl + `/orgs/${orgId}/additionalPaymentMethodTypes`,
		);
	}

	public static async updateDefaultPaymentMethod(
		orgId: number,
		paymentMethodType: PaymentType,
		billingApproved: boolean,
		paymentMethodActive: boolean,
		accountName?: string,
		accountNumber?: string,
	): Promise<AxiosResponse<any>> {
		// TODO: Type this.
		const paymentInfoParams: OrdwayPaymentInfoParams = this.constructPaymentInfo(
			paymentMethodType,
			billingApproved,
			paymentMethodActive,
			accountName,
			accountNumber,
		);
		return await axios.patch(this.baseUrl + '/ordway/customer/updatePaymentInfo', {
			levnoOrgId: orgId,
			...paymentInfoParams,
		});
	}

	public static fetchPaymentMethodsForOrg(
		orgId: number,
	): Promise<AxiosResponse<OrgPaymentMethodResponse>> {
		return axios.get<OrgPaymentMethodResponse>(this.baseUrl + `/orgs/${orgId}/paymentMethods`);
	}

	public static updatePaymentMethod(
		orgId: number,
		updatedPaymentMethod: OrgPaymentMethod,
	): Promise<AxiosResponse<OrgPaymentMethodResponse>> {
		return axios.patch<OrgPaymentMethodResponse>(
			this.baseUrl + `/orgs/${orgId}/paymentMethod/${updatedPaymentMethod.id}`,
			{ data: { ...updatedPaymentMethod } },
		);
	}

	public static async updateSubscription(
		productId: number,
		directPlan: OrdwayPlan,
		partnerId?: number | null,
		partnerPlan?: OrdwayPlan | null,
	): Promise<{
		message: string;
		error?: AxiosError | undefined;
		shouldToast?: boolean | undefined;
	} | void> {
		let updatePlansRequest: UpdateOrdwayPlansParams = {
			productId,
			ordwayDirectPlanId: directPlan.id,
			ordwayDirectPlanName: directPlan.name,
			ordwayDirectPlanProductTier: directPlan.levno_product_tier,
		};
		if (partnerId && partnerPlan) {
			updatePlansRequest.partnerId = partnerId;
			updatePlansRequest.ordwayPartnerPlanId = partnerPlan.id;
			updatePlansRequest.ordwayPartnerPlanName = partnerPlan.name;
		}
		try {
			await axios.post(`/products/${productId}/updateOrdwayPlans`, updatePlansRequest);
		} catch (error) {
			return {
				error,
				message: 'Ordway plans failed to update. Please let someone from the software team know.',
			};
		}
	}

	public static async updateActiveSubscription(
		allOrdwayPlans: OrdwayPlan[],
		product: { siteId: number; orgId: number; id: number; supplier: string },
		supplierHasChanged: boolean,
		productOrdwayAttributes: ProductOrdwayAttributes,
		directPlanId: string,
		partnerProductOrdwayAttributes: ProductOrdwayAttributes | null,
		partnerPlanId?: string,
	): Promise<{ message: string; error?: AxiosError; shouldToast?: boolean }> {
		if (directPlanId !== productOrdwayAttributes.ordwayPlanId) {
			// Direct plan has changed - we need to change the direct subscription's plan (they are one to one).
			const selectedPlan: OrdwayPlan | undefined = allOrdwayPlans.find(
				(plan: OrdwayPlan) => plan.id === directPlanId,
			);
			if (selectedPlan !== undefined) {
				await axios.patch('/ordway/updateActiveSubscription', {
					siteId: product.siteId,
					orgId: product.orgId,
					productId: product.id,
					newOrdwayPlanId: selectedPlan.id,
					charges: [],
				});
			}
		}
		return { message: 'Success' };
	}

	public static async updateDraftPlan(
		allOrdwayPlans: OrdwayPlan[],
		product: { siteId: number; orgId: number; id: number; supplier: string },
		supplierHasChanged: boolean,
		productOrdwayAttributes: ProductOrdwayAttributes,
		directPlanId: string,
		partnerProductOrdwayAttributes: ProductOrdwayAttributes | null,
		partnerPlanId?: string,
	): Promise<{ message: string; error?: AxiosError; shouldToast?: boolean }> {
		if (productOrdwayAttributes !== null) {
			if (this.planHasChanged(directPlanId, productOrdwayAttributes)) {
				await this.handleDraftDirectSubscriptionUpdate(allOrdwayPlans, directPlanId, product);
			}
		}
		if (partnerProductOrdwayAttributes !== null) {
			if (this.planHasChanged(partnerPlanId!, partnerProductOrdwayAttributes)) {
				if (partnerPlanId === '') {
					await this.deleteDraftPartnerSubscription(partnerProductOrdwayAttributes);
				} else {
					const newPartnerPlan = this.getSelectedPlan(allOrdwayPlans, partnerPlanId!);
					await this.updateDraftPartnerSubscription(
						partnerProductOrdwayAttributes,
						newPartnerPlan!.id,
					);
				}
			}
		} else {
			await this.handleNewPartnerSubscription(allOrdwayPlans, partnerPlanId!, product);
		}

		return { message: 'Success' };
	}

	public static async isOnNewOrdwayPlan(
		product: Product | null,
		productOrdwayAttribute: ProductOrdwayAttributes | null,
		billingApi: BillingApi = new BillingApi(),
	): Promise<boolean> {
		if (product === null || !product.type) {
			return false;
		}
		try {
			const newOrdwayPlansResponse = await billingApi.getProductDirectPlans();
			const newOrdwayPlans: DirectPlansInData = newOrdwayPlansResponse.data;
			if (productOrdwayAttribute === null) {
				return true;
			}
			if (!newOrdwayPlans.data) {
				return false;
			}
			const planId: string = productOrdwayAttribute.ordwayPlanId;
			switch (product.type) {
				case 'milk':
					return newOrdwayPlans.data.milk!.directPlanId === planId;
				case 'fuel':
					return newOrdwayPlans.data.fuel!.directPlanId === planId;
				case 'water':
					return newOrdwayPlans.data.water!.directPlanId === planId;
				case 'feed':
					return newOrdwayPlans.data.feed!.directPlanId === planId;
				default:
					return false;
			}
		} catch {
			return false;
		}
	}

	public static async canChangeProductOrg(product: Product): Promise<boolean> {
		if (!product.id || !product.orgId) {
			return false;
		}
		try {
			await this.fetchProductOrdwayAttributes(product.id, product.orgId);
			return false; // Business logic is to only let product org be changed if there are NO billing attributes for the product. I.e. this call DOES error..
		} catch (e) {
			return true;
		}
	}

	private static async handleDraftDirectSubscriptionUpdate(
		allOrdwayPlans: OrdwayPlan[],
		directPlanId: string,
		product: { orgId: number; siteId: number; id: number },
	) {
		const selectedPlan = allOrdwayPlans.find((plan: OrdwayPlan) => plan.id === directPlanId);
		if (selectedPlan !== undefined) {
			try {
				await this.updateDraftOrdwaySubscription({
					siteId: product.siteId,
					orgId: product.orgId,
					productId: product.id,
					newOrdwayPlanId: selectedPlan.id,
					charges: [],
				});
			} catch (error) {
				return {
					error,
					message:
						'Updating direct plan in Ordway failed, please retry and / or let the software team know.',
				};
			}
		}
	}

	private static async handleNewPartnerSubscription(
		allOrdwayPlans: OrdwayPlan[],
		partnerPlanId: string,
		product: {
			siteId: number;
			orgId: number;
			id: number;
			supplier: string;
		},
	) {
		// We previously had no partner plan, now we do and need to create prodOrdwayAttrs and subscription in Ordway.
		const newPartnerPlan = this.getSelectedPlan(allOrdwayPlans, partnerPlanId!);
		if (newPartnerPlan !== undefined) {
			try {
				await this.addNewPartnerSubscription(product, newPartnerPlan);
			} catch (error) {
				return {
					error,
					message:
						'Updating partner plan in Ordway failed, please retry and / or let the software team know.',
				};
			}
		}
	}

	private static getSelectedPlan(
		allOrdwayPlans: OrdwayPlan[],
		planId: string,
	): OrdwayPlan | undefined {
		return allOrdwayPlans.find((plan: OrdwayPlan) => plan.id === planId);
	}

	private static async updateDraftPartnerSubscription(
		partnerProductOrdwayAttributes: ProductOrdwayAttributes,
		newPartnerPlanId: string,
	) {
		await axios.patch('/ordway/partner/subscription/draft', {
			productOrdwayAttributeId: partnerProductOrdwayAttributes.id,
			newOrdwayPlanId: newPartnerPlanId,
		});
	}

	private static async deleteDraftPartnerSubscription(
		partnerProductOrdwayAttributes: ProductOrdwayAttributes,
	) {
		await axios.patch('/ordway/partner/subscription/deleteDraft', {
			productOrdwayAttributeId: partnerProductOrdwayAttributes.id,
		});
	}

	private static planHasChanged(
		planId: string,
		productOrdwayAttributes: ProductOrdwayAttributes,
	): boolean {
		return planId != productOrdwayAttributes.ordwayPlanId;
	}

	private static readonly baseUrl: string = process.env.VUE_APP_API_URL.replace(/\/+$/, '');

	private static async updateDraftOrdwaySubscription(
		draftSubscriptionUpdateParams: OrdwayDraftSubscriptionUpdateParams,
	): Promise<void> {
		await axios.patch(this.baseUrl + '/ordway/subscription/draft', {
			...draftSubscriptionUpdateParams,
		});
	}

	private static async addNewPartnerSubscription(
		product: { siteId: number; orgId: number; id: number; supplier: string },
		selectedPartnerPlan: OrdwayPlan,
	) {
		await axios.post(this.baseUrl + '/ordway/partner/subscription/create', {
			productId: product.id,
			siteId: product.siteId,
			partnerPlanId: selectedPartnerPlan.id,
			partnerPlanName: selectedPartnerPlan.name,
			partnerPlanProductTier: selectedPartnerPlan.levno_product_tier,
			supplier: product.supplier,
		});
	}

	private static constructPaymentInfo(
		type: PaymentType,
		billingApproved: boolean,
		paymentMethodActive: boolean,
		accountName?: string,
		accountNumber?: string,
	): OrdwayPaymentInfoParams {
		switch (type as PaymentType) {
			case 'farmSource':
			case 'ruralCo':
			case 'directDebit': {
				return {
					type,
					accountName,
					accountNumber,
					billingApproved,
					paymentMethodActive,
				};
			}
			case 'directInvoice':
			default: {
				return {
					type: 'directInvoice',
					billingApproved: true,
					paymentMethodActive: true,
				};
			}
		}
	}
}
